/* global document, window */
import * as React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, makeStyles } from "@fluentui/react-components";
import CommonTooltip from "../../common/CommonTooltip";
import { balance, merge, split, splitAll, swap } from "../../../state/structureSlice";
import { swap as swapStyles } from "../../../state/cellStylesSlice";
import { select } from "../../../state/selectionSlice";
import { BalanceHorizontal, BalanceVertical, Copy, Merge, Paste, Split, SplitAll, Swap } from "../../../assets/icons";
import { batchGroupBy } from "../../../helpers/batchGroupBy";
import TrackedButton from "../../common/TrackedButton";
import { ContentType } from "../../../helpers/copyPaste";

const useClasses = makeStyles({
  Header: {
    fontFamily: "Arial",
    fontSize: 10,
    fontWeight: 700,
    lineGeight: 11.5,
    textAlign: "left",
  },
  PanelButton: {
    justifyContent: "left",
    fontFamily: "Arial",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: "0.02em",
  },
  AccordionHeader: {
    "& > button": {
      minHeight: 0,
    },
  },
});

const MergeMenuItem = ({ isKeyboardSelected = false }) => {
  const classes = useClasses();
  const { t } = useTranslation();
  const selection = useSelector((state) => state.selection);
  const structure = useSelector((state) => state.gridd.present.structure);
  const preferences = useSelector((state) => state.preferences);
  const dispatch = useDispatch();

  const isMergedCellSelected = (cells) => {
    if (!cells || cells.length !== 1 || !structure.merged) return false;
    const cell = cells[0];
    return structure.merged.some((m) => m.cell.x === cell.x && m.cell.y === cell.y);
  };

  const getAllSelectedCells = () => {
    const allCells = [];
    const cellMap = new Map();

    // Add direct cell selections
    selection.cells.forEach((cell) => {
      const key = `${cell.x},${cell.y}`;
      if (!cellMap.has(key)) {
        cellMap.set(key, cell);
        allCells.push(cell);
      }
    });

    // Add full row selections
    if (selection.rows.length > 0) {
      selection.rows.forEach((rowIndex) => {
        for (let y = 0; y < structure.columns.length; y++) {
          const key = `${rowIndex},${y}`;
          if (!cellMap.has(key)) {
            const structCell = structure.cells[rowIndex][y];
            const selCell = { id: structCell.id, x: rowIndex, y };
            cellMap.set(key, selCell);
            allCells.push(selCell);
          }
        }
      });
    }

    // Add full column selections
    if (selection.columns.length > 0) {
      selection.columns.forEach((colIndex) => {
        for (let x = 0; x < structure.rows.length; x++) {
          const key = `${x},${colIndex}`;
          if (!cellMap.has(key)) {
            const structCell = structure.cells[x][colIndex];
            const selCell = { id: structCell.id, x, y: colIndex };
            cellMap.set(key, selCell);
            allCells.push(selCell);
          }
        }
      });
    }

    return allCells;
  };

  const canMerge = () => {
    const allCells = getAllSelectedCells();
    if (!allCells || allCells.length < 2) return false;

    const allInSameRow = allCells.every((cell) => cell.x === allCells[0].x);
    const allInSameColumn = allCells.every((cell) => cell.y === allCells[0].y);

    if (!allInSameRow && !allInSameColumn) return false;

    if (allInSameRow) {
      // Horizontal merge attempt — disable if any cell is part of a vertical (dir="down") merge
      return !allCells.some((cell) =>
        structure.merged?.some(
          (m) => m.dir === "down" && m.cell.y === cell.y && m.cell.x <= cell.x && cell.x <= m.cell.x + m.length
        )
      );
    }

    // Vertical merge attempt — disable if any cell is part of a horizontal (dir="right") merge
    return !allCells.some((cell) =>
      structure.merged?.some(
        (m) => m.dir === "right" && m.cell.x === cell.x && m.cell.y <= cell.y && cell.y <= m.cell.y + m.length
      )
    );
  };

  useEffect(() => {
    if (!isKeyboardSelected) return;

    const menuButtons = document.querySelectorAll('[id$="_panel_button"]');
    let currentFocusIndex = 0;

    if (menuButtons.length > 0) {
      menuButtons[0].focus();
    }

    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex + 1) % menuButtons.length;
          menuButtons[currentFocusIndex].focus();
          break;

        case "ArrowUp":
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex - 1 + menuButtons.length) % menuButtons.length;
          menuButtons[currentFocusIndex].focus();
          break;

        case "Enter":
          event.preventDefault();
          menuButtons[currentFocusIndex].click();
          break;

        case "F4":
          // Reset focus and keyboard navigation
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isKeyboardSelected]);

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateRows: "auto auto auto",
          border: isKeyboardSelected ? "2px solid #000000" : "none",
        }}
      >
        <Button
          id="merge_menu_home_merge_cells_panel_button"
          icon={<Merge />}
          disabled={!canMerge()}
          appearance="subtle"
          className={classes.PanelButton}
          onClick={() => {
            const allCells = getAllSelectedCells();
            let topLeft;
            let bottomRight;
            for (let i = 0; i < allCells.length; i++) {
              const cell = allCells[i];
              // Find if this cell is part of a merged cell
              const merge = structure.merged?.find(
                (m) =>
                  m.cell.x <= cell.x &&
                  m.cell.y <= cell.y &&
                  m.cell.x + (m.dir === "down" ? m.length : 0) >= cell.x &&
                  m.cell.y + (m.dir === "right" ? m.length : 0) >= cell.y
              );

              // Get the actual dimensions considering any existing merge
              const effectiveX = merge ? (merge.dir === "down" ? merge.cell.x + merge.length : merge.cell.x) : cell.x;
              const effectiveY = merge ? (merge.dir === "right" ? merge.cell.y + merge.length : merge.cell.y) : cell.y;

              if (topLeft) {
                // For topLeft we want the minimum coordinates
                topLeft = cell.x < topLeft.x || cell.y < topLeft.y ? cell : topLeft;
              } else topLeft = cell;

              if (bottomRight) {
                // For bottomRight we want the maximum effective coordinates
                bottomRight =
                  effectiveX > bottomRight.x || effectiveY > bottomRight.y
                    ? { x: effectiveX, y: effectiveY, id: cell.id }
                    : bottomRight;
              } else bottomRight = { x: effectiveX, y: effectiveY, id: cell.id };
            }

            if (topLeft && bottomRight) {
              let length = 1;
              let dir = "right";
              if (topLeft.x < bottomRight.x) {
                length = bottomRight.x - topLeft.x;
                dir = "down";
              } else if (topLeft.y < bottomRight.y) {
                length = bottomRight.y - topLeft.y;
              }

              dispatch(merge({ cell: { ...topLeft, id: undefined }, length, dir }));
              dispatch(select({ cell: { ...topLeft, id: topLeft.id }, type: "clear" }));
            }
          }}
        >
          {t("menu.merge.cells")}
        </Button>
        <Button
          id="merge_menu_home_split_cells_panel_button"
          icon={<Split />}
          appearance="subtle"
          className={classes.PanelButton}
          disabled={!isMergedCellSelected(selection.cells)}
          onClick={() => {
            dispatch(split({ cell: selection.cells[0] }));
          }}
        >
          {t("menu.merge.split")}
        </Button>
        <Button
          id="merge_menu_home_split_all_cells_panel_button"
          icon={<SplitAll />}
          appearance="subtle"
          className={classes.PanelButton}
          onClick={() => {
            dispatch(splitAll());
          }}
        >
          {t("menu.merge.split.all")}
        </Button>
        <CommonTooltip content={t("menu.merge.swap.tooltip")} positioning="below">
          <Button
            id="merge_menu_home_swap_cells_panel_button"
            icon={<Swap />}
            disabled={selection.cells?.length !== 2 && selection.rows?.length !== 2 && selection.columns?.length !== 2}
            appearance="subtle"
            className={classes.PanelButton}
            onClick={() => {
              batchGroupBy.start();
              if (selection.cells?.length === 2) {
                dispatch(swap({ cells: [selection.cells[0], selection.cells[1]] }));
                if (preferences?.cellSwapStyles) {
                  dispatch(swapStyles({ cells: [selection.cells[0], selection.cells[1]] }));
                }
              } else if (selection.rows?.length === 2) {
                dispatch(swap({ rows: selection.rows }));
              } else if (selection.columns?.length === 2) {
                dispatch(swap({ columns: selection.columns }));
              }
              batchGroupBy.end();
            }}
          >
            {t("menu.merge.swap")}
          </Button>
        </CommonTooltip>
        {(selection.table === true || selection.rows.length > 1) && (
          <CommonTooltip content={t("menu.merge.balance.horizontal")} positioning="below">
            <Button
              id="merge_menu_home_balance_all_rows_panel_button"
              className={classes.PanelButton}
              appearance="subtle"
              icon={<BalanceHorizontal />}
              onClick={() => {
                dispatch(
                  balance({ rows: selection.table === true ? structure.rows.map((_, i) => i) : selection.rows })
                );
              }}
            >
              {t("menu.merge.balance.horizontal")}
            </Button>
          </CommonTooltip>
        )}
        {(selection.table === true || selection.columns.length > 1) && (
          <CommonTooltip content={t("menu.merge.balance.vertical")} positioning="below">
            <Button
              id="merge_menu_home_balance_all_columns_panel_button"
              className={classes.PanelButton}
              appearance="subtle"
              icon={<BalanceVertical />}
              onClick={() => {
                dispatch(
                  balance({
                    columns: selection.table === true ? structure.columns.map((_, i) => i) : selection.columns,
                  })
                );
              }}
            >
              {t("menu.merge.balance.vertical")}
            </Button>
          </CommonTooltip>
        )}
        <CommonTooltip content={t("menu.merge.copy.tooltip")} positioning="below">
          <TrackedButton
            id="merge_menu_copy_button"
            icon={<Copy />}
            appearance="subtle"
            className={classes.PanelButton}
            onClick={(e) => {
              window.clipboardWithContext?.onCopy(e);
            }}
          >
            {t("menu.merge.copy")}
          </TrackedButton>
        </CommonTooltip>
        <CommonTooltip content={t("menu.merge.paste.tooltip")} positioning="below">
          <TrackedButton
            id="merge_menu_paste_button"
            icon={<Paste />}
            appearance="subtle"
            className={classes.PanelButton}
            onClick={async () => {
              const parsed = await window.clipboardWithContext?.tryParseClipboard();
              if (parsed?.type === ContentType.Table || parsed?.type === ContentType.Gridd) {
                window.clipboardWithContext?.onParsed(parsed);
              }
            }}
          >
            {t("menu.merge.paste")}
          </TrackedButton>
        </CommonTooltip>
      </div>
    </div>
  );
};

MergeMenuItem.propTypes = {
  isKeyboardSelected: PropTypes.bool,
};

export default MergeMenuItem;
